import {Injectable} from '@angular/core';
import {BehaviorSubject, from, Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {AmplifyService} from 'aws-amplify-angular';

import {Case} from '../../_models/case.model';
import {Data} from '../../_models/data.model';
import {Card} from '../../_models/card.model';
import {RefData} from '../../_models/ref.data.model';
import {Entity} from '../../_models/entity.model';
import {Assignment} from '../../_models/assignment.model';
import {Review} from '../../_models/review.model';
import {ReviewTracking} from '../../_models/review.tracking.model';
import {User} from '../../_models/user.model';
import {ExportFile} from '../../_models/export.file.model';
import {environment} from '../../../environments/environment';
import {Router} from '@angular/router';
import {Part} from '../../_models/part.model';
import * as uuid from 'uuid';
import {AuthenticationService} from '../../core/services/authentication.service';
import { SessionManagementService } from '../../core/services/session-management.service';
import { Constants } from '../../shared/constants';

@Injectable({
  providedIn: 'root',
})
export class CrisisAlertService {
  private readonly apiName: string;
  private headers = {'Content-Type': 'application/json'};
  private user: User;
  private casesSub = new BehaviorSubject<Case[]>([]);
  readonly casesObs = this.casesSub.asObservable();
  private cardsSub = new BehaviorSubject<Card[]>([]);
  readonly cardsObs = this.cardsSub.asObservable();
  private serverTimeSub = new BehaviorSubject<number>(0);
  readonly serverTimeObs = this.serverTimeSub.asObservable();
  private specialityNameSub = new BehaviorSubject<string>('General');
  readonly specialityNameObs = this.specialityNameSub.asObservable();
  public specialityCodeSub = new BehaviorSubject<string>('GNRL');
  private refDataSub = new BehaviorSubject<RefData>(new RefData());
  readonly refDataObs = this.refDataSub.asObservable();
  private pageSizeSub = new BehaviorSubject<number>(20);
  readonly pageSizeObs = this.pageSizeSub.asObservable();
  private pageSub = new BehaviorSubject<number>(1);
  readonly pageObs = this.pageSub.asObservable();
  public defaultQuery = 'specialityCode=GNRL&caseStatus=NR&caseStatus=IP';
  public searchQuerySub = new BehaviorSubject<string>(this.defaultQuery);
  readonly searchQueryObs = this.searchQuerySub.asObservable();
  private listTriggerSub = new BehaviorSubject<string>('card');
  readonly listTriggerObs = this.listTriggerSub.asObservable();
  private searchTriggerSub = new BehaviorSubject<boolean>(false);
  readonly searchTriggerObs = this.searchTriggerSub.asObservable();
  public refDataStore: RefData = {
    specialities: [{name: 'General', code: 'GNRL'}],
    tags: [], programs: [], tests: [], reviewers: [], statuses: []
  };
  private filesSub = new BehaviorSubject<ExportFile[]>(null);
  readonly filesObs = this.filesSub.asObservable();
  public fileSearchQuerySub = new BehaviorSubject<string>(null);
  readonly fileSearchQueryObs = this.fileSearchQuerySub.asObservable();
  private downloadSearchTriggerSub = new BehaviorSubject<boolean>(false);
  readonly downloadSearchTriggerObs = this.downloadSearchTriggerSub.asObservable();


  constructor(private amplifyService: AmplifyService,
              private sessionManagementService: SessionManagementService,
              private router: Router) {
    this.apiName = environment.api.endpoints[0].name;
  }

  private pushCases(cases: Case[]) {
    this.casesSub.next(cases);
  }

  private pushCards(cards: Card[]) {
    this.cardsSub.next(cards);
  }

  private pushServerTime(epoch: number) {
    this.serverTimeSub.next(epoch);
  }

  private pushSpecialtyName(specialty: string) {
    this.specialityNameSub.next(specialty);
  }

  private pushSpecialtyCode(code: string) {
    this.specialityCodeSub.next(code);
  }

  pushSearchQuery(query: string) {
    this.searchQuerySub.next(query);
  }

  private pushRefData(refData: RefData) {
    this.refDataSub.next(refData);
  }

  pushListTrigger(source: string) {
    this.listTriggerSub.next(source);
  }

  pushPageSize(pageSize: number) {
    this.pageSizeSub.next(pageSize);
  }

  pushPageNum(page: number) {
    this.pageSub.next(page);
  }

  pushSearchTrigger(isTriggered: boolean): void {
    this.searchTriggerSub.next(isTriggered);
  }

  pushDownloadSearchTrigger(isTriggered: boolean): void {
    this.downloadSearchTriggerSub.next(isTriggered);
  }

  pushFiles(files: ExportFile[]) {
    this.filesSub.next(files);
  }

  pushFileSearchQuery(query: string) {
    this.fileSearchQuerySub.next(query);
  }

  getSpecialityName(specialityCode: string): string {
    return this.refDataStore.specialities.find(item => item.code === specialityCode).name;
  }

  getSpecialityCode(specialityName: string): string {
    return this.refDataStore.specialities.find(item => item.name === specialityName).code;
  }

  public getCaseDB(id: number | string, userId: number): Observable<any> {
    const url = `/search/cases/${id}?userId=${userId}`;
    const content = {
      headers: this.getHeaders(),
    };
    return from(this.amplifyService.api().get(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - GetCase CaseID=${id}`)),
      catchError(this.handleError<Case>(`getCase()`, this.buildFailCase(`${id}`)))
    );
  }

  loadAlerts(specialityCode: string) {
    this.searchCases(specialityCode, `specialityCode=${specialityCode}&caseStatus=NR&caseStatus=IP`);
  }

  searchCases(specialityCode: string, queryString) {
    this.getCases(queryString).subscribe(data => {
      this.pushSearchQuery(queryString);
      if (specialityCode && 'ALL' !== specialityCode) {
        this.pushSpecialtyCode(specialityCode);
        this.pushSpecialtyName(this.getSpecialityName(specialityCode));
      }
      this.pushCases(data.cases);
      this.pushCards(data.cards);
      this.pushServerTime(data.systemTime);
    }, error => console.log('API Resp - Could not load cases.'));
  }

  private getCases(query: string): Observable<any> {
    if (!query.trim()) {
      return of(new Data([], []));
    }
    const url = `/search/cases?${query}`;
    const content = {
      headers: this.getHeaders(),
    };
    return from(this.amplifyService.api().get(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - GetCases Query=${query}`)),
      catchError(this.handleError<Data>('getCases()', new Data([], [])))
    );
  }

  private getRefData(): Observable<any> {
    const url = '/ref-data';
    const content = {
      headers: this.getHeaders(),
    };
    return from(this.amplifyService.api().get(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - GetRefData`)),
      catchError(this.handleError<RefData>('getRefData()', new RefData()))
    );
  }

  private loadRefData(): void {
    this.getRefData().subscribe(data => {
      this.pushRefData(data);
      this.refDataStore = data;
    }, error => console.log('Could not load RefData.'));
  }

  public loadRefDataFirstTime(user: User): void {
    // set user
    this.user = user;
    this.loadRefData();
  }

  public ensureRefData(): void {
    if (this.refDataStore.programs.length === 0) {
      this.loadRefData();
    }
  }

  public getEntity(entityId: string, entityValue: string): Observable<any> {
    const query = 'entityId=' + entityId + '&entityValue=' + entityValue;
    const url = `/entities?${query}`;
    const content = {
      headers: this.getHeaders(),
    };
    return from(this.amplifyService.api().get(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - getEntity query=${query}`)),
      catchError(this.handleError<Entity>('getEntity()', new Entity('', entityId))));
  }

  public extendAssignmentForUser(assignment: Assignment): void {
    if (assignment) {
      this.updateAssignment(assignment).subscribe(x => {
        console.log('API Resp - Updated Assignment ' + `${assignment.assignmentId}`);
      }, error => console.log('API Resp - Could not updateAssignment.' + `${assignment.assignmentId}` + error));
    }
  }

  public releaseAssignmentForUser(assignment: Assignment): void {
    if (assignment) {
      assignment.expiryMinutes = 0;
      this.updateAssignment(assignment).subscribe(x => {
        console.log('API Resp - Updated Assignment');
      }, error => console.log('API Resp - Could not updateAssignment.' + error));
    }
  }

  public reviewContentTrackingOfUser(tracking: ReviewTracking): void {
    this.saveReviewTracking(tracking).subscribe(x => {
      console.log('API Resp - ReviewTrackingID =' + x.reviewTrackingId);
    }, error => console.log('API Resp - Could not saveReviewTracking .' + error));
  }

  updateCaseStatus(userId: string, caseId: string, status: string): Observable<Case> {
    const aCase = new Case();
    aCase.reviewerId = userId;
    aCase.caseId = caseId;
    aCase.caseStatusCode = status;
    return this.updateCase(aCase);
  }

  updateAssignment(assignment: Assignment): Observable<any> {
    const url = `/search/cases/${assignment.caseId}/assignments`;
    const content = {
      headers: this.getHeaders(),
      body: assignment
    };
    return from(this.amplifyService.api().post(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - UpdateAssignment CaseID=${assignment.caseId}`)),
      catchError(this.handleError<Assignment>('getEntity()', new Assignment())));
  }

  saveReviewTracking(tracking: ReviewTracking): Observable<any> {
    const url = `/search/cases/${tracking.caseId}/review-tracking`;
    const content = {
      headers: this.getHeaders(),
      body: tracking
    };
    return from(this.amplifyService.api().put(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - SaveReviewTracking CaseID=${tracking.caseId}`)),
      catchError(this.handleError<ReviewTracking>('getEntity()', new ReviewTracking())));
  }

  updateCase(aCase: Case): Observable<any> {
    const url = `/search/cases/${aCase.caseId}`;
    const content = {
      headers: this.getHeaders(),
      body: aCase
    };
    return from(this.amplifyService.api().patch(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - Update CaseID=${aCase.caseId}`)),
      catchError(this.handleError<Case>('updateCaseStatus()', new Case())));
  }

  saveReview(review: Review): Observable<any> {
    const url = `/search/cases/${review.caseId}/reviews`;
    const content = {
      headers: this.getHeaders(),
      body: review
    };
    return from(this.amplifyService.api().put(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - SaveReview CaseID=${review.caseId}`)),
      catchError(this.handleError<Review>('getEntity()', new Review())));
  }

  getFileFromS3(filePath: string): Observable<any> {
    return from(this.amplifyService.storage().get(filePath)).pipe(
      tap(_ => this.log(`S3Storage Call - GetFileFromS3 URL=${filePath}`)),
      catchError(this.handleError<string>('getFileFromS3()', '')));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // route if critical
      this.checkForCriticalErrors(operation, error.message);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  private buildFailCase(id: string) {
    let part: Part;
    part = {
      partType: 'PART',
      partId: '1408',
      viewerCode: 'ONE',
      mimeCode: 'TXT',
      uri: null,
      uriSrcType: null,
      waveFormUri: null,
      data: '',
      comment: null,
      airResponse: null
    };
    let failCase: Case;
    failCase = {
      errorMsg: 'data-fail',
      specialityCode: null,
      programCode: null,
      testCode: null,
      grade: '',
      candidateId: '',
      caseStatus: '',
      caseStatusCode: '',
      reviewerName: '',
      reviewerId: '',
      receiptDate: null,
      caseId: id,
      specialityName: '',
      programName: 'GRE',
      testName: '',
      contentTypeCode: 'RSPNS',
      parts: [part],
      assignment: null,
      testDate: '',
      flaggedDate: '',
      candidateInitials: '',
      promptSetName: '',
      promptSetCode: ''
    };
    return failCase;
  }


  /** Log a CaseService message with the MessageService */
  private log(message: string) {
    console.log(message);
  }

  private checkForCriticalErrors(operation: string, error: string) {
    if ('getRefData()' === operation) {
      this.router.navigate(['error']);
    }
  }

  searchExportFiles(query: string): void {
    this.getExportFiles(query).subscribe(files => {
      this.pushFileSearchQuery(query);
      this.pushFiles(files);
    }, error => console.log('Could not load exported files.'));
  }

  getExportFiles(query: string): Observable<any> {
    if (!query.trim()) {
      return of([]);
    }
    const url = `/exported-files?${query}`;
    const content = {
      headers: this.getHeaders(),
    };
    return from(this.amplifyService.api().get(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - GetExportFiles Query=${query}`)),
      catchError(this.handleError<ExportFile[]>('getExportFiles()', []))
    );
  }

  addAuditInfoForDownloadFile(file: ExportFile): void {
    this.updateExportFile(file).subscribe(dataFile => console.log('Updated fileId=' + dataFile.fileId));
  }

  updateExportFile(file: ExportFile): Observable<any> {
    const url = `/exported-files/${file.fileId}`;
    const content = {
      headers: this.getHeaders(),
      body: file
    };
    return from(this.amplifyService.api().patch(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - UpdateExportFile FileID=${file.fileId}`)),
      catchError(this.handleError<ExportFile>('updateExportFile()', new ExportFile())));
  }

  addCaseDB(_case: Case): Observable<any> {
    const url = '/search/cases';
    const content = {
      headers: this.getHeaders(),
      body: _case
    };
    return from(this.amplifyService.api().put(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API Call - addCaseDB`)),
      catchError(this.handleError<Case>('addCaseDB()', new Case())));
  }

  uploadFileToS3(file: File, path: string): Observable<any> {

    /*this.amplifyService.storage().put(`${path}/${file.name}`, file, {contentType: file.type})
      .then(result => {
        this.log(`Success uploading file!`);
      })
      .catch(err => {
        this.log(`Cannot uploading file: ${err}`);
        alert('The system failed to process the selected file.');
        // this.handleError<Case>('addCaseDB()', new Case());
        // TODO write rollback code or mark Tech Difficulty
      }); */
    return from(this.amplifyService.storage().put(`${path}/${file.name}`, file, {contentType: file.type})).pipe(
      tap(_ => this.log(`API Call - uploadFileToS3`)),
      catchError(this.handleError<string>('uploadFileToS3()', 'fail')));
  }

  private getHeaders() {
    this.sessionManagementService.extendSession();
    if (!this.user) {
      this.user = JSON.parse(sessionStorage.getItem(Constants.AUTH_TOKEN));
      if (!this.user) {
        return {
          'Content-Type': 'application/json',
          'guid': uuid.v4(),
          'userid': '-1',
          'sessionid': 'first_call',
          'requestid': uuid.v4()
        };
      }
    }
    return {
      'Content-Type': 'application/json',
      'guid': this.user.guid,
      'userid': String(this.user.userId),
      'sessionid': String(this.user.loginHistoryId),
      'requestid': uuid.v4()
    };
  }

}
